import type { SeriesClickCallbackFunction } from 'highcharts';
import { useMemo } from 'react';

interface RadarChartOptions {
  subtitle?: string;
  onClick?: SeriesClickCallbackFunction;
  enableLegend?: boolean;
  /**
   * Formats the aggregated point value for tooltips and data labels (e.g. the
   * 2-dp consequence cost formatter). When omitted, the raw `{point.y}` string
   * formats are retained unchanged (RSP-5735).
   */
  valueFormatter?: (value: number) => string;
}

/**
 * Builds the radar tooltip point string for a formatted value. Exported so the
 * `this`-bound Highcharts callback can stay thin and be unit-tested without
 * faking a full `Highcharts.Point` context.
 */
export const formatRadarTooltipPoint = (
  seriesName: string | undefined,
  value: number,
  valueFormatter: (value: number) => string
): string => `${seriesName ?? ''}: ${valueFormatter(value)}<br/>`;

export const useGetRadarChartDefaultOptions = ({
  subtitle,
  onClick,
  enableLegend,
  valueFormatter,
}: RadarChartOptions): Highcharts.Options => {
  return useMemo(() => {
    const options: Highcharts.Options = {
      chart: {
        polar: true,
      },
      subtitle: {
        text: subtitle || '',
      },
      xAxis: {
        tickmarkPlacement: 'between',
        lineWidth: 0,
      },
      yAxis: {
        gridLineInterpolation: 'circle',
        lineWidth: 0,
        min: 0,
        allowDecimals: false,
        endOnTick: false,
      },
      legend: {
        enabled: enableLegend ?? false,
      },
      plotOptions: {
        series: {
          events: {
            click: onClick,
          },
          ...(valueFormatter
            ? {
                dataLabels: {
                  formatter: function radarDataLabelFormatter(
                    this: Highcharts.Point
                  ) {
                    return valueFormatter(this.y ?? 0);
                  },
                },
              }
            : {}),
        },
        column: {
          pointPadding: 0,
          groupPadding: 0,
        },
      },
      tooltip: {
        shared: true,
        headerFormat: '<b>{category}</b><br/>',
        ...(valueFormatter
          ? {
              pointFormatter: function radarTooltipPointFormatter(
                this: Highcharts.Point
              ) {
                return formatRadarTooltipPoint(
                  this.series.name,
                  this.y ?? 0,
                  valueFormatter
                );
              },
            }
          : { pointFormat: '{series.name}: {point.y}<br/>' }),
      },
    };

    return options;
  }, [subtitle, enableLegend, onClick, valueFormatter]);
};
